import { useCallback, useEffect } from "react";
import { Provider, CreateProvider, UpdateProvider } from "../types/models/Provider.d";
import { UpdateEntityProvider } from "../types/models/Provider";
import { useGetProviderListQuery, useInsertProviderMutation, useUpdateProviderMutation, useDeleteProviderMutation, } from "../apis/Provider.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useProvider(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertProviderMutation, { 
        isLoading: insertProviderLoading, 
        isSuccess: insertProviderIsSuccess,
        isError: insertProviderIsError,
        error: insertProviderError,
    }] = useInsertProviderMutation()
    const [updateProviderMutation, { 
        isLoading: updateProviderLoading,
        isSuccess: updateProviderIsSuccess,
        isError: updateProviderIsError,
        error: updateProviderError,
    }] = useUpdateProviderMutation()
    const [deleteProviderMutation, { 
        isLoading: deleteProviderLoading,
        isSuccess: deleteProviderIsSuccess,
        isError: deleteProviderIsError,
        error: deleteProviderError,
    }] = useDeleteProviderMutation()

    const loading =
        insertProviderLoading ||
        updateProviderLoading ||
        deleteProviderLoading ||
        false

    const insertProvider = useCallback(async (provider: CreateProvider, redirectPath: string|undefined) => {
        try {
            const data = await insertProviderMutation(provider).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertProviderMutation, navigate])
    
    useEffect(() => {
        if (insertProviderIsSuccess) {
            openSuccessAlert('Provider created')
        } else if (insertProviderIsError) {
            console.log("add error", insertProviderError)
            openFailAlert(t`Provider add error: ` + insertProviderError?.data?.message)
        }
    }, [
        insertProviderIsSuccess,
        insertProviderIsError,
        insertProviderError
    ]);

    const updateProvider = useCallback(async (provider: UpdateEntityProvider, redirectPath: string|undefined) => {
        try {
            const data = await updateProviderMutation(provider).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateProviderMutation, navigate])
    
    useEffect(() => {
        if (updateProviderIsSuccess) {
            openSuccessAlert(t`Provider updated`)
        } else if (updateProviderIsError) {
            console.log("update error", updateProviderError)
            openFailAlert(t`Provider update error: ` + updateProviderError?.data?.message)
        }
    }, [
        updateProviderIsSuccess,
        updateProviderIsError,
        updateProviderError
    ]);

    const deleteProvider = useCallback((provider: Provider, redirectPath: string|undefined) => {
        openDialog(async () => {
            provider = {
                ...provider,
            }
            try {
                const { data } = await deleteProviderMutation(provider);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteProviderMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteProviderIsSuccess) {
            openSuccessAlert(t`Provider deleted`)
        } else if (deleteProviderIsError) {
            console.log("delete error", deleteProviderError)
            openFailAlert(t`Provider delete error: ` + deleteProviderError?.data?.message)
        }
    }, [
        deleteProviderIsSuccess,
        deleteProviderIsError,
        deleteProviderError
    ]);

    return {
        loading,
        insertProvider,
        updateProvider,
        deleteProvider
    }
}
